<template>
  <main-logo-register />

  <FormCard
    :image-src="content.src"
    :title="content.title"
    :description="content.description"
    :card-style="{ maxWidth: '700px', paddingLeft: '50px', paddingRight: '50px', 
      display: 'inline-grid', alignContent: 'center', minWidth: 'min(670px, 95%)' }"
    :class="{ admin: isOwner }"
    preLine
  >
    <section class="drawing" v-if="!isOwner">
      <img :src="billingLockImage" alt="padlock" />
    </section>
  </FormCard>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import { computed, reactive } from "vue";
import dayjs, { type Dayjs } from "dayjs";

import { useValidateWorkspace } from "@/app/auth/composables/useValidateWorkspace";

import FormCard from "@/app/auth/components/atoms/FormCard.vue";
import MainLogoRegister from "@/app/shared/components/molecules/MainLogoRegister.vue";
import billingLockImage from "@/app/settings/assets/billing-lock.png?url";
import { ReasonInactivate } from "@/app/common/types";

/**
 * Query params
 *
 * slot: string;
 * admin: "True" | "False";
 * date_payment: string;
 * blocked_at: string;
 * price: string;
 * slots_invoice: string;
 */

const route = useRoute();
const workspaceData = useValidateWorkspace();

const formatDate = (date: string | Dayjs) => dayjs(date).format("DD/MM/YYYY");

const isOwner = computed(() => route.query.is_owner == 'true');
const isClientPartner = computed(() => route.query.is_client_partner == 'true');
const typeReasonDeactivation = computed(() => route.query.type as ReasonInactivate);

const titleOwner = computed(() => {
  if (typeReasonDeactivation.value == ReasonInactivate.NotRenewContract) {
    return isClientPartner.value
      ? `Tu ambiente de trabajo fue bloqueado el  
        ${formatDate(String(route.query.blocked_at).slice(0, 18))} 😥
        Comunícate con tu partner para reactivarla`
      : `Tu ambiente de trabajo fue bloqueado el 
        ${formatDate(String(route.query.blocked_at).slice(0, 18))} 😥
        Comunicate con un asesor para renovar tu
        contrato.`
      
  } else if (typeReasonDeactivation.value == ReasonInactivate.NotPayPlan) {
    return isClientPartner.value
      ? `Tu ambiente de trabajo fue inactivado el  
        ${formatDate(String(route.query.blocked_at).slice(0, 18))} 😥
        Comunícate con tu partner para realizar el pago de tu plan y reactivar tu ambiente.`
      : `Tu ambiente de trabajo fue bloqueado el 
        ${formatDate(String(route.query.blocked_at).slice(0, 18))} 😥
        Comunicate con un asesor para pagar tu plan.`
  } else {
    return `Tu ambiente de trabajo fue bloqueado el 
      ${formatDate(String(route.query.blocked_at).slice(0, 18))} 😥
      Comunicate con un asesor para reactivarla.`
  }
});

const content = computed(() => ({
  src: isOwner.value ? billingLockImage : workspaceData.value?.picture,
  title: isOwner.value ? titleOwner.value :
    `El ambiente de trabajo de ${workspaceData.value?.name}
      se encuentra bloqueado temporalmente 😥 `
  ,
  description: isOwner.value
    ? `El acceso a la plataforma está restringido para todos los usuarios.
      Recomendamos retomar su actividad pronto. \n
      ${isClientPartner.value
        ? "Para más información comunícate con tu partner."
        : "Si tienes alguna duda, puedes escribirnos a support@beexcc.com"}`
    : "Por favor, comuníquese con su administrador ante cualquier duda. ",
}));
</script>

<style scoped lang="sass">
.content
  display: flex
  flex-direction: column
  align-items: center

  h3
    text-align: center
    font-size: 24px
    color: $gray-9
    font-weight: 600
    margin: 0

.drawing
  text-align: center
img
  margin-top: 48px

.admin:deep(.card .caption h2)
  color: $gray-8 !important

table
  width: 70%
  margin: 45px auto

td
  width: 50%
  padding: 7px 0

tr:not(:last-child)
  border-bottom: 1px solid $text-cuaternary
</style>
